// The Work create use-case behind the composer's send: a submission routes to
// nothing (blocked), a routine launch, the unavailable-skill toast, or the create
// command the host runs. What the app owns crosses from the binding at
// `src/features/projects/components/ProjectsLandingComposer.tsx`: the routine run
// and the route the launch opens.
import { toast } from '@heidi/design-system/toast';
import { useIntl } from 'react-intl';

import {
  buildComposerSubmission,
  composerSkillToastMessages,
  formatCompileSegmentedTaskError,
  type ProjectsComposerSkillReference,
} from '../../data';
import {
  useRoutineLaunchOperation,
  type RoutineLaunchOperationOptions,
} from '../../data/projects/useRoutineLaunchOperation';
import type { WorkCreateCommand, WorkCreateSubmit } from './Composer';
import { composerUploadedAttachmentMetadata } from './composer-attachments';
import { projectRunFlagsForCreate } from './ProjectRunOptionsSelector.utils';

export interface WorkCreateSubmitOptions extends Pick<
  RoutineLaunchOperationOptions,
  'runRoutine' | 'onNavigate'
> {
  availableSkills: readonly ProjectsComposerSkillReference[];
  disabled: boolean;
  sendDisabled: boolean;
  isSubmitting: boolean;
  onCreate: (command: WorkCreateCommand) => void;
  onCreated?: () => void;
  onLaunchingChange?: (workflowId: string | null) => void;
}

export const useWorkCreateSubmit = ({
  availableSkills,
  disabled,
  sendDisabled,
  isSubmitting,
  onCreate,
  onCreated,
  onLaunchingChange,
  runRoutine,
  onNavigate,
}: WorkCreateSubmitOptions): WorkCreateSubmit => {
  const intl = useIntl();
  const launchRoutine = useRoutineLaunchOperation({
    runRoutine,
    onNavigate,
    onLaunchingChange,
  });

  const onSubmit: WorkCreateSubmit = (payload, prepareCreate) => {
    const submission = buildComposerSubmission({
      draft: payload.draft,
      routinesOffered: true,
      offeredSkills: availableSkills,
      linkedItems: payload.linkedContext,
      runFlags: payload.runFlags ?? {},
      approvalMode: payload.approvalMode ?? null,
      disabled,
      sendDisabled,
      isSubmitting,
    });
    switch (submission.kind) {
      case 'blocked':
        return;
      case 'workflow': {
        const attachmentIds = composerUploadedAttachmentMetadata(payload.attachments).map(
          (file) => file.attachment_id
        );
        launchRoutine(submission.workflowId, {
          prompt: submission.prompt || undefined,
          attachmentIds: attachmentIds.length > 0 ? attachmentIds : undefined,
          sessionId: submission.sessionId,
          patientProfileId: submission.patientProfileId,
        });
        return;
      }
      case 'skill_unavailable':
        toast({
          title: intl.formatMessage(composerSkillToastMessages.title),
          description: formatCompileSegmentedTaskError(submission.error, intl),
          tone: 'destructive',
        });
        return;
      case 'submit': {
        const command: WorkCreateCommand = {
          text: submission.text,
          displayText: submission.displayText,
          attachments: payload.attachments,
          linkedContext: payload.linkedContext,
          runFlags: projectRunFlagsForCreate(submission.runFlags),
          computerUseSkill: submission.computerUseSkill,
          approvalMode: submission.approvalMode,
        };
        const prepared = prepareCreate ? prepareCreate(command) : command;
        if (!prepared) return;
        onCreate(prepared);
        onCreated?.();
      }
    }
  };

  return onSubmit;
};
